import { Injectable } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { RestTimezone } from '@main-application/management/pages/system-configuration/components/date-time-configuration/model/timezone';
import { TimezoneEntityHelper } from '@main-application/management/pages/system-configuration/components/timezone-entity.helper';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestAssociateTicketInfo, RestTurnoverDataModelNew } from '@shared/interfaces/turnover.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { SummaryFilterItem } from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';

import { RestTurnStepScheduleBaseModel, RestTurnStepScheduleModel } from '../interfaces/board';
import { BoardType, ScheduleCellStatus } from '../interfaces/board.enums';

export interface TurnStepScheduleListModel {
  unitName?: string;
  unitId: number;
  propertyName?: string;
  propertyId: number;
  turnStepScheduleId: number;
  columnName?: string;
  boardName?: string;
  boardId: number;
  assigneeId: number;
  assigneeName?: string;
  assigneeIsInactive: boolean;
  status: TurnStepScheduleStatus;
  originalDueDate?: string;
  dueDate?: string;
  notifyWhenDoneUserIds?: number[];
  completedOn?: string;
  amount?: number;
  poNumber?: string;
  columnId: string;
}

export interface RestTurnStepScheduleListModel {
  unitName?: string;
  unitId: number;
  propertyName?: string;
  propertyId: number;
  turnStepScheduleId: number;
  columnName?: string;
  boardName?: string;
  boardId: number;
  assigneeId?: number;
  assigneeName?: string;
  assigneeIsInactive?: boolean;
  status: TurnStepScheduleStatus;
  originalDueDate?: string;
  dueDate?: string;
  notifyWhenDoneUserIds?: number[];
  completedOn?: string;
  amount?: number;
  poNumber?: string;
  columnId: string;
}

export interface TurnStepScheduleListQuery {
  portfolioId?: number;
  propertyIds?: string;
  startDate?: string;
  endDate?: string;
  queryType?: QueryType;
  statuses?: string;
  boardType?: BoardType;
  dateFieldsLookupMode?: DateFieldsLookupMode;
  assigneeId?: number;
  boardId?: number;
  scheduleByStatusFilter?: ScheduleByStatusFilter;
  filters?: SummaryFilterItem[];
}

export enum QueryType {
  ActiveTurnBoardsOnly = 0,
  CompletedTurnBoardsOnly = 1,
  All = 2,
}

export enum TurnsQueryType {
  AllTurns,
  ActiveTurns,
  CompletedTurns,
}

export enum DateFieldsLookupMode {
  DueOrCreated = 0,
  DueOnly = 1,
  CompletedOn = 2,
  AddedToBoardDate = 3,
}

export enum TurnStepScheduleStatus {
  Due = 0,
  Skip = 1,
  Completed = 2,
  Late = 3,
  AmountOnly = 4,
  Pending = 5,
  Declined = 6,
  InProgress = 7,
  Unscheduled = 99,
}

export const TurnStepScheduleStatusConstList: IRadioButtonOption<TurnStepScheduleStatus>[] = [
  {
    label: 'Due',
    value: TurnStepScheduleStatus.Due,
  },
  {
    label: 'Skipped',
    value: TurnStepScheduleStatus.Skip,
  },
  {
    label: 'Completed',
    value: TurnStepScheduleStatus.Completed,
  },
  {
    label: 'Late',
    value: TurnStepScheduleStatus.Late,
  },
  {
    label: 'Amount Only',
    value: TurnStepScheduleStatus.AmountOnly,
  },
  {
    label: 'Pending',
    value: TurnStepScheduleStatus.Pending,
  },
  {
    label: 'Declined',
    value: TurnStepScheduleStatus.Declined,
  },
  {
    label: 'In Progress',
    value: TurnStepScheduleStatus.InProgress,
  },
  {
    label: 'Unscheduled',
    value: TurnStepScheduleStatus.Unscheduled,
  },
];

export enum ScheduleByStatusFilter {
  Total,
  TodayDueCompleted,
  Due,
  DueToday,
  DueLate,
  Pending,
  PendingLate,
  Moved,
  MovedLate,
  Started,
  StartedLate,
  TotalIncomplete,
  Completed,
  CompletedOnTime,
  CompletedLate,
  TotalLate,
  CompletedLate24h,
  CompletedLate48h,
  CompletedLate72hPlus,
  OpenLessThan30Days,
  Open30DaysPlus,
  NonCompletedTotal,
  NonCompletedTotalLate,
  Due24h,
  Due48h,
  Due72hPlus,
  Upcoming,
  Unscheduled,
  NotStarted,
  NotStartedLate,
}

export const ScheduleByStatusFilterLabels: Record<ScheduleByStatusFilter, string> = {
  [ScheduleByStatusFilter.Total]: 'Total',
  [ScheduleByStatusFilter.TodayDueCompleted]: 'Due today (completed)',
  [ScheduleByStatusFilter.Due]: 'Due',
  [ScheduleByStatusFilter.DueToday]: 'Due today',
  [ScheduleByStatusFilter.DueLate]: 'Due (late)',
  [ScheduleByStatusFilter.Pending]: 'Pending',
  [ScheduleByStatusFilter.PendingLate]: 'Pending (late)',
  [ScheduleByStatusFilter.Moved]: 'Moved',
  [ScheduleByStatusFilter.MovedLate]: 'Moved (late)',
  [ScheduleByStatusFilter.Started]: 'Started',
  [ScheduleByStatusFilter.StartedLate]: 'Started (late)',
  [ScheduleByStatusFilter.TotalIncomplete]: 'Incomplete',
  [ScheduleByStatusFilter.Completed]: 'Completed',
  [ScheduleByStatusFilter.CompletedOnTime]: 'Completed on time',
  [ScheduleByStatusFilter.CompletedLate]: 'Completed (late)',
  [ScheduleByStatusFilter.TotalLate]: 'Late',
  [ScheduleByStatusFilter.CompletedLate24h]: 'Completed (24 hours late)',
  [ScheduleByStatusFilter.CompletedLate48h]: 'Completed (48 hours late)',
  [ScheduleByStatusFilter.CompletedLate72hPlus]: 'Completed (over 72 hours late)',
  [ScheduleByStatusFilter.OpenLessThan30Days]: 'Open < 30 days',
  [ScheduleByStatusFilter.Open30DaysPlus]: 'Open 30+ days',
  [ScheduleByStatusFilter.NonCompletedTotal]: 'Incomplete',
  [ScheduleByStatusFilter.NonCompletedTotalLate]: 'Incomplete (late)',
  [ScheduleByStatusFilter.Due24h]: 'Due in 24 hours',
  [ScheduleByStatusFilter.Due48h]: 'Due in 48 hours',
  [ScheduleByStatusFilter.Due72hPlus]: 'Due in 72 or more hours',
  [ScheduleByStatusFilter.Upcoming]: 'Upcoming',
  [ScheduleByStatusFilter.Unscheduled]: 'Unscheduled',
  [ScheduleByStatusFilter.NotStarted]: 'Not Started',
  [ScheduleByStatusFilter.NotStartedLate]: 'Not Started (late)',
};

export interface BoardListParams {
  queryType?: QueryType;
  dateFieldsLookupMode?: DateFieldsLookupMode;
  portfolioId?: number;
  propertyIds?: string;
  collectionIds?: string;
  boardType?: BoardType;
  boardId?: number;
  assigneeId?: number;
  startDate?: string;
  endDate?: string;
  scheduleByStatusFilter?: ScheduleByStatusFilter;
  statuses?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TurnStepScheduleService {
  private readonly route = 'turnStepSchedule/';

  constructor(private service: RestapiService, private snackbarService: SnackbarService) {}

  private static fixTimezoneForTurnStepScheduleModel(data: RestTurnStepScheduleModel[], timezone: RestTimezone) {
    data.forEach(e => TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToClient(e, timezone));
    return data;
  }

  getList(turnoverId: number) {
    return this.service.getData<RestTurnStepScheduleModel[]>(
      this.route + 'list/' + turnoverId,
      TurnStepScheduleService.fixTimezoneForTurnStepScheduleModel
    );
  }

  getBoardList(params: BoardListParams) {
    const queryParams = new URLSearchParams();

    if (params.queryType !== undefined && params.queryType !== null) {
      queryParams.set('queryType', params.queryType.toString());
    }

    if (params.dateFieldsLookupMode !== undefined && params.dateFieldsLookupMode !== null) {
      queryParams.set('dateFieldsLookupMode', params.dateFieldsLookupMode.toString());
    }

    if (params.scheduleByStatusFilter !== undefined && params.scheduleByStatusFilter !== null) {
      queryParams.set('scheduleByStatusFilter', params.scheduleByStatusFilter.toString());
    }

    if (params.portfolioId !== undefined && params.portfolioId !== null) {
      queryParams.set('portfolioId', params.portfolioId.toString());
    }

    if (params.propertyIds !== undefined && params.propertyIds !== null) {
      queryParams.set('propertyIds', params.propertyIds.toString());
    }

    if (params.boardType !== undefined && params.boardType !== null) {
      queryParams.set('boardType', params.boardType.toString());
    }

    if (params.boardId !== undefined && params.boardId !== null) {
      queryParams.set('boardId', params.boardId.toString());
    }

    if (params.statuses !== undefined && params.statuses !== null) {
      queryParams.set('statuses', params.statuses.toString());
    }

    if (params.assigneeId !== undefined && params.assigneeId !== null) {
      queryParams.set('assigneeId', params.assigneeId.toString());
    }

    if (params.startDate !== undefined && params.startDate !== null) {
      queryParams.set('startDate', params.startDate);
    }

    if (params.endDate !== undefined && params.endDate !== null) {
      queryParams.set('endDate', params.endDate);
    }

    if (params.collectionIds !== undefined && params.collectionIds !== null) {
      queryParams.set('collectionIds', params.collectionIds.toString());
    }

    const route = `${this.route}list?${queryParams.toString()}`;
    return this.service.getData<TurnStepScheduleListModel[]>(route, null);
  }

  add(entity: RestTurnStepScheduleModel, turn: RestTurnoverDataModelNew) {
    const addEntity = {
      ...entity,
      pushToPMS: entity.pushToPMS,
      ticketTopicId: entity.ticketTopicId,
    };
    return this.service
      .post<RestTurnStepScheduleModel>(
        this.route + 'add',
        addEntity,
        TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToServer,
        TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToClient
      )
      .pipe(tap(data => this.updateDates(data, turn, entity)));
  }

  update(entity: RestTurnStepScheduleModel, turn: RestTurnoverDataModelNew) {
    const correctedStatusEntity =
      entity.status === ScheduleCellStatus.Moved ? { ...entity, status: ScheduleCellStatus.Due } : entity;
    //BE do not know that we have Moved status and store it as Due
    const updateEntity = {
      ...correctedStatusEntity,
      pushToPMS: entity.pushToPMS,
      ticketTopicId: entity.ticketTopicId,
    };
    return this.service
      .post<RestTurnStepScheduleModel>(
        this.route + 'update',
        updateEntity,
        TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToServer,
        TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToClient
      )
      .pipe(tap(data => this.updateDates(data, turn, entity)));
  }

  doneAll(entity: RestTurnStepScheduleModel, turn: RestTurnoverDataModelNew, boardId: number) {
    return this.service
      .post<RestTurnStepScheduleModel>(
        this.route + 'doneAll',
        { turnoverId: entity.turnoverId, boardLayoutId: boardId },
        null,
        TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToClient
      )
      .pipe(tap(data => this.updateDates(data, turn, entity)));
  }

  delete(turnStepScheduleId: number, turn: RestTurnoverDataModelNew) {
    return this.deleteSchedule(turnStepScheduleId).pipe(tap(data => this.updateDates(data, turn, null)));
  }

  deleteSchedule(turnStepScheduleId: number) {
    return this.service.post<RestTurnStepScheduleBaseModel>(
      this.route + `delete/${turnStepScheduleId}`,
      null,
      null,
      TimezoneEntityHelper.fixTimezoneForTurnStepScheduleModelToClient
    );
  }

  associateTicket(ticket: RestAssociateTicketInfo) {
    return this.service.post<RestTurnStepScheduleModel>(this.route + `associateTicket`, ticket);
  }

  private updateDates(
    stepScheduleModel: RestTurnStepScheduleBaseModel,
    turn: RestTurnoverDataModelNew,
    schedule: RestTurnStepScheduleModel
  ) {
    if (stepScheduleModel.dateCompleted && schedule) {
      schedule.dateCompleted = stepScheduleModel.dateCompleted;
    }
    if (stepScheduleModel.turnoverDateAvailable && turn) {
      turn.dateAvailable = stepScheduleModel.turnoverDateAvailable;
    }
    if (stepScheduleModel.turnoverDateShowable && turn) {
      turn.dateShowable = stepScheduleModel.turnoverDateShowable;
    }

    if (stepScheduleModel.originalDueDate && schedule) {
      schedule.originalDueDate = stepScheduleModel.originalDueDate;
    }
  }

  getTurnStepScheduleList(filter: TurnStepScheduleListQuery): Observable<RestTurnStepScheduleListModel[]> {
    return this.service.post<RestTurnStepScheduleListModel[]>(`${this.route}list`, filter).pipe(
      catchError(err => {
        const error = err?.errorCode ?? SnackbarErrorMessage.RetrievingTicketList;
        this.snackbarService.error(error);
        return of([]);
      })
    );
  }
}
